import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon, Flag as FlagIcon } from "@mui/icons-material";
import { createNewMilestone } from "store/features/company/projectManagement/milestone/milestoneActions";
import DateRangeCalendar from "components/calendar/DateRangeCalendar";

const toInputDate = (value) => (value ? String(value).split("T")[0] : "");

const AddMilestoneModal = ({ isOpen, onClose, onSuccess, projectId, project }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    start_date: "",
    due_date: "",
  });

  const projectStartDate = toInputDate(project?.start_date);
  const projectEndDate = toInputDate(project?.end_date);

  const dateError =
    formData.start_date &&
      formData.due_date &&
      formData.start_date > formData.due_date
      ? "Start date cannot be after due date"
      : formData.start_date &&
        projectStartDate &&
        formData.start_date < projectStartDate
        ? "Milestone cannot start before project start date"
        : formData.due_date && projectEndDate && formData.due_date > projectEndDate
          ? "Milestone cannot exceed project end date"
          : "";

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({ title: "", description: "", start_date: "", due_date: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return;
    if (dateError) return;

    setLoading(true);
    const submitData = {
      project_id: projectId,
      title: formData.title,
      description: formData.description,
      start_date: formData.start_date,
      due_date: formData.due_date,
    };

    const result = await dispatch(createNewMilestone(submitData));

    if (result?.success) {
      onSuccess?.();
      onClose();
      resetForm();
    }
    setLoading(false);
  };

  const isDark = theme.palette.mode === "dark";

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: isDark ? "#1e293b" : "#f9fafb",
      "& fieldset": {
        borderColor: isDark ? "#475569" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#f59e0b",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f59e0b",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: "text.secondary",
      position: "relative",
      transform: "none",
      marginBottom: "8px",
      display: "block",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "primary.main",
    },
    "& .MuiInputLabel-asterisk": {
      color: "#ef4444",
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              backgroundColor: isDark
                ? "rgba(252, 226, 181, 0.14)"
                : "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(40px)",
            }}
          />

          {/* Modal slide-in from right */}
          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            style={{
              position: "fixed",
              right: 0,
              top: 0,
              zIndex: 1301,
              height: "100%",
              width: "100%",
              maxWidth: "520px",
              overflowY: "auto",
              backgroundColor: isDark ? "#0f172a" : "#ffffff",
              boxShadow: theme.shadows[24],
            }}
          >
            {/* Header */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                borderBottom: `1px solid ${isDark ? "#1e293b" : "#f3f4f6"}`,
                backgroundColor: isDark ? "#0f172a" : "#ffffff",
              }}
            >
              <Box sx={{ p: 3, pr: 7 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background:
                        "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                      flexShrink: 0,
                    }}
                  >
                    <FlagIcon sx={{ color: "#fff", fontSize: 24 }} />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontWeight: "bold",
                        color: "text.primary",
                      }}
                    >
                      Add Milestone
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.5, color: "text.secondary" }}
                    >
                      Add a new milestone to this project
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <IconButton
                onClick={onClose}
                sx={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  color: "text.secondary",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f59e0b",
                    color: "#ffffff",
                    transform: "scale(1.05)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <Box sx={{ p: 3 }}>
                <Stack spacing={2.5}>
                  <TextField
                    fullWidth
                    label="Milestone Title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    placeholder="e.g. Planning"
                    sx={inputSx}
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    placeholder="e.g. Requirement gathering"
                    sx={inputSx}
                    InputLabelProps={{ shrink: true }}
                  />

                  {/* Reusable Date Range Calendar Component */}
                  <DateRangeCalendar
                    startDate={formData.start_date}
                    dueDate={formData.due_date}
                    onChange={({ startDate, endDate }) => {
                      setFormData((prev) => ({
                        ...prev,
                        start_date: startDate,
                        due_date: endDate,
                      }));
                    }}
                    minDate={projectStartDate}
                    maxDate={projectEndDate}
                    label="Milestone Timeline"
                    startLabel="Start Date"
                    endLabel="Due Date"
                    error={dateError}
                    accentColor="#f59e0b"
                  />
                </Stack>
              </Box>

              <Box
                sx={{
                  p: 3,
                  borderTop: `1px solid ${isDark ? "#1e293b" : "#f3f4f6"}`,
                  display: "flex",
                  gap: 2,
                }}
              >
                <Button
                  onClick={onClose}
                  disabled={loading}
                  fullWidth
                  variant="outlined"
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    borderColor: isDark ? "#475569" : "#d1d5db",
                    color: isDark ? "#d1d5db" : "#374151",
                    "&:hover": {
                      borderColor: "#f59e0b",
                      backgroundColor: isDark
                        ? "rgba(245,158,11,0.1)"
                        : "#fffbeb",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    loading ||
                    !formData.title.trim() ||
                    !formData.start_date ||
                    !formData.due_date ||
                    Boolean(dateError)
                  }
                  fullWidth
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    color: "#ffffff",
                    boxShadow: "0 10px 15px -3px rgba(245, 158, 11, 0.25)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                      transform: "scale(1.02)",
                    },
                    "&:disabled": { opacity: 0.7 },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                  ) : (
                    "Add Milestone"
                  )}
                </Button>
              </Box>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AddMilestoneModal;
